/**
 * Inventory UI — US 01.05/01.08, pure DOM. A 5×3 grid of 15 slots, opened
 * by intent, details on tap (no hover-exclusive information — US 12.04),
 * trash with explicit confirmation, Eat for consumable categories, and the
 * corpse-run bag collection (US 01.07).
 *
 * Every sync it performs is broadcast as 'client-state' — the HUD, the bag
 * sprite and the pending markers all feed on it.
 */
import { worldBus, type WorldToUiEvents } from '@shared/bus.js';
import { sessionState } from '@shared/session-state.js';
import { callDialogue } from '@transport/api.js';
import { button, confirmPrompt, itemSlot, toast as showToast } from './design/components.js';
import { showDeathScreen } from './prompts.js';

interface Stack {
  itemId: string;
  qty: number;
  slot: number;
}
interface SyncState {
  inventory: Stack[];
  money: number;
  health: number;
  stateVersion: number;
  pendingNpcs: { npcId: string; count: number }[];
  deathDrop: { x: number; y: number; items: Stack[] } | null;
  knownSpecies: string[];
  // Present on 'sync' responses only (US 03.x) — city-level info.
  dayInfo?: { day: number; daysUntilCutover: number | null };
  hazards?: WorldToUiEvents['city-info']['hazards'];
  areas?: WorldToUiEvents['city-info']['areas'];
  fishingSpots?: WorldToUiEvents['city-info']['fishingSpots'];
}
interface ConsumeResult {
  wasPoisonous: boolean;
  healthDelta: number;
  died: boolean;
  speciesNowKnown: boolean;
  state: SyncState;
}
interface ItemMeta {
  id: string;
  name: string;
  description: string;
  category: string;
}

const EDIBLE_CATEGORIES = new Set(['food', 'produce', 'fish']);

export function initInventoryPanel(root: HTMLElement): {
  refresh: () => void;
  itemName: (id: string) => string;
} {
  const panel = document.createElement('div');
  panel.className = 'wca-inventory hidden';
  root.appendChild(panel);

  const items = new Map<string, ItemMeta>();
  void fetch(
    `${import.meta.env.VITE_SUPABASE_URL}/rest/v1/items?select=id,name,description,category`,
    { headers: { apikey: import.meta.env.VITE_SUPABASE_ANON_KEY ?? '' } },
  )
    .then((r) => r.json())
    .then((rows: ItemMeta[]) => {
      for (const row of rows) items.set(row.id, row);
    })
    .catch(() => undefined);

  const itemName = (id: string) => items.get(id)?.name ?? id;

  let open = false;
  let latest: SyncState | null = null;

  const toast = (text: string, danger = false) => showToast(root, text, danger ? 'danger' : 'info');

  function broadcast(state: SyncState): void {
    latest = state;
    worldBus.emit('client-state', {
      money: state.money,
      health: state.health,
      deathDrop: state.deathDrop,
      pendingNpcs: state.pendingNpcs,
      hasBicycle: state.inventory.some((s) => s.itemId === 'itm_bicycle'),
      hasBoat: state.inventory.some((s) => s.itemId.startsWith('itm_boat')),
    });
    // City info rides only on full syncs; mirror it for a scene that boots
    // after the first sync landed (US 03.x).
    if (state.dayInfo && state.hazards && state.areas) {
      const info = {
        dayInfo: state.dayInfo,
        hazards: state.hazards,
        areas: state.areas,
        ...(state.fishingSpots ? { fishingSpots: state.fishingSpots } : {}),
      };
      sessionState.cityInfo = info;
      worldBus.emit('city-info', info);
    }
    if (open) render();
  }

  async function handleDeath(state: SyncState): Promise<void> {
    const bag = state.deathDrop;
    worldBus.emit('player-died', { x: bag?.x ?? 0, y: bag?.y ?? 0 });
    await showDeathScreen(
      root,
      { lostItems: bag?.items ?? [], lostMoney: 0 /* summarised server-side later */ },
      itemName,
    );
  }

  function render(): void {
    panel.innerHTML = '';
    const head = document.createElement('div');
    head.className = 'wca-inventory-head';
    head.textContent = `Inventory — ${latest?.money ?? 0} money · ${latest?.health ?? 100} hp`;

    const grid = document.createElement('div');
    grid.className = 'wca-inventory-grid';
    const bySlot = new Map((latest?.inventory ?? []).map((s) => [s.slot, s]));
    for (let i = 0; i < 15; i++) {
      const stack = bySlot.get(i);
      if (stack) {
        const meta = items.get(stack.itemId);
        grid.appendChild(
          itemSlot(meta?.name ?? stack.itemId, stack.qty, () => showDetails(stack, meta)),
        );
      } else {
        const cell = document.createElement('button');
        cell.className = 'wca-slot';
        cell.disabled = true;
        grid.appendChild(cell);
      }
    }

    const details = document.createElement('div');
    details.className = 'wca-inventory-details';
    details.textContent = 'Tap an item for details.';

    function showDetails(stack: Stack, meta: ItemMeta | undefined): void {
      details.innerHTML = '';
      const p = document.createElement('p');
      const known = latest?.knownSpecies.includes(stack.itemId);
      const edibleCategory = EDIBLE_CATEGORIES.has(meta?.category ?? '');
      const edibility = edibleCategory ? (known ? '' : ' Edibility: unknown.') : '';
      p.textContent = `${meta?.name ?? stack.itemId} ×${stack.qty} — ${meta?.description ?? ''}${edibility}`;
      details.appendChild(p);

      if (edibleCategory) {
        details.appendChild(
          button('Eat one', () => {
            void callDialogue<ConsumeResult>({ op: 'consume', itemId: stack.itemId }).then(
              async (r) => {
                if (!r.ok) return;
                const { wasPoisonous, healthDelta, died, state } = r.result;
                if (died) {
                  toast('That was a terrible idea.', true);
                  broadcast(state);
                  await handleDeath(state);
                } else if (wasPoisonous) {
                  toast(`Poisonous! ${healthDelta} hp`, true);
                  broadcast(state);
                } else {
                  toast(`Tasty. +${healthDelta} hp`);
                  broadcast(state);
                }
              },
            );
          }),
        );
      }

      details.appendChild(
        button('Store at home', () => {
          void callDialogue({ op: 'store_item', itemId: stack.itemId, qty: stack.qty }).then(
            (r) => {
              if (r.ok) {
                toast('Stored for future weeks.');
                refresh();
              } else toast(r.message || 'Cannot store that', true);
            },
          );
        }),
      );

      details.appendChild(
        button(
          'Trash one',
          () => {
            // Explicit confirmation; trashing permanently destroys (US 01.05),
            // via the styled prompt — touch-safe, never window.confirm (12.04).
            void confirmPrompt(
              root,
              `Destroy 1 × ${meta?.name ?? stack.itemId}? This is permanent.`,
              'Destroy it',
            ).then((yes) => {
              if (!yes) return;
              void callDialogue({ op: 'trash', itemId: stack.itemId, qty: 1 }).then((r) => {
                if (r.ok) {
                  toast('Item destroyed.', true); // US 17.05: item destroyed
                  refresh();
                }
              });
            });
          },
          'destructive',
        ),
      );
    }

    panel.append(head, grid, details);
  }

  function refresh(): void {
    void callDialogue<SyncState>({ op: 'sync' }).then((res) => {
      if (res.ok) broadcast(res.result);
    });
  }

  function toggle(): void {
    open = !open;
    panel.classList.toggle('hidden', !open);
    if (open) {
      render();
      refresh();
    }
  }

  worldBus.on('ui-intent', ({ intent }) => {
    if (intent === 'OPEN_INVENTORY') toggle();
    if (intent === 'CANCEL' && open) toggle();
  });

  // Corpse run (US 01.07): the world layer says the player pressed INTERACT
  // next to the bag; collection fills what fits, the bag keeps the rest.
  worldBus.on('bag-collect-requested', () => {
    void callDialogue<{ collected: { itemId: string; qty: number }[]; state: SyncState }>({
      op: 'collect_drops',
    }).then((r) => {
      if (!r.ok) return;
      const got = r.result.collected.reduce((n, c) => n + c.qty, 0);
      toast(got > 0 ? `Recovered ${got} item(s).` : 'No room to carry anything.');
      broadcast(r.result.state);
    });
  });

  // Hazard hits (US 03.07): the world detects the touch, the server decides
  // whether it counts (invulnerability, authored damage, death cascade).
  worldBus.on('hazard-collision', ({ hazardId }) => {
    void callDialogue<{ healthDelta: number; died: boolean; state: SyncState }>({
      op: 'hazard_hit',
      hazardId,
    }).then(async (r) => {
      if (!r.ok) return; // INVULNERABLE — the client flash raced the server
      const { healthDelta, died, state } = r.result;
      toast(`Hit by a car! ${healthDelta} hp`, true);
      broadcast(state);
      if (died) await handleDeath(state);
    });
  });

  return { refresh, itemName };
}
